import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useAsync, useAsyncFn} from 'react-use';
import {
  createUavPointCloudsUseCase,
  deleteUavPointCloudsUseCase,
  downloadGpkgForestMassUseCase,
  downloadUavPointCloud,
  getAllAppraisersUseCase,
  getAllForestMassStatusesUseCase,
  getAllInventoryPlotsByForestMassUuidUseCase,
  getAllLidarTechnologiesUseCase,
  getAllSpeciesUseCase,
  getCurrentUserUseCase,
  getForestMassByUuidUseCase,
  getForestMassSpeciesByForestMassUuidUseCase,
  getUavPointCloudsByForestMassUuidUseCase,
  opCreateInventoryPlot,
  opCutForestMassUseCase,
  publishErrorUseCase,
  reUploadUavPointCloud,
  updateForestMassSpeciesUseCase,
  updateForestMassUseCase,
} from '@/compositionRoot';
import type {BackState} from '@/domain/commonTypes';
import type {ForestMass} from '@/domain/entities/ForestMass';
import type {ForestMassSpecies} from '@/domain/entities/ForestMassSpecies';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import type {CreateInventoryPlot, CutForestMass} from '@/domain/entities/Op';
import type {UavPointCloudCreate} from '@/domain/entities/UavPointCloud';
import businessLogicBasedOnStatuses from '@/domain/useCases/businessLogicBasedOnStatuses';
import UserPermissions from '@/domain/useCases/userPermissions';

export const useController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {uuid} = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [forestMass, setForestMass] = useState<ForestMass | undefined>(undefined);
  const [forestMassSpecies, setForestMassSpecies] = useState<Array<ForestMassSpecies>>();
  const [forestMassStatuses, setForestMassStatuses] = useState<Array<ForestMassStatus>>();

  const {loading: loadingForestMass} = useAsync(
    () => getForestMassByUuidUseCase.execute(uuid || '').then(setForestMass),
    [uuid],
  );
  const {loading: loadingForestMassSpecies} = useAsync(
    () => getForestMassSpeciesByForestMassUuidUseCase.execute(uuid || '').then(setForestMassSpecies),
    [uuid],
  );

  const [forestMassStatusesState, fetchForestMassStatuses] = useAsyncFn(
    () => getAllForestMassStatusesUseCase.execute().then(setForestMassStatuses),
    [uuid],
  );
  const loadingForestMassStatuses = forestMassStatusesState.loading;
  const [appraisers, fetchAppraisers] = useAsyncFn(() => getAllAppraisersUseCase.execute(), [uuid]);
  const [species, fetchSpecies] = useAsyncFn(() => getAllSpeciesUseCase.execute(), [uuid]);
  const [user, fetchUser] = useAsyncFn(() => getCurrentUserUseCase.execute(), []);
  const [lidarTechnologies, fetchLidarTechnologies] = useAsyncFn(() => getAllLidarTechnologiesUseCase.execute(), []);
  const [uavPointCloud, fetchUavPointClouds] = useAsyncFn(() => {
    if (uuid) return getUavPointCloudsByForestMassUuidUseCase.execute(uuid);
    return Promise.resolve();
  }, [uuid]);
  const [inventoryPlots, fetchInventoryPlots] = useAsyncFn(() => {
    if (uuid) return getAllInventoryPlotsByForestMassUuidUseCase.execute(uuid);
    return Promise.resolve();
  }, [uuid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Not necessary
  useEffect(() => {
    fetchAppraisers();
    fetchSpecies();
    fetchUser();
    fetchLidarTechnologies();
    fetchUavPointClouds();
    fetchInventoryPlots();
    fetchForestMassStatuses();
  }, []);

  useEffect(() => {
    if (
      appraisers.loading ||
      species.loading ||
      loadingForestMassStatuses ||
      user.loading ||
      loadingForestMass ||
      loadingForestMassSpecies
    ) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    appraisers.loading,
    species.loading,
    loadingForestMassStatuses,
    user.loading,
    loadingForestMass,
    loadingForestMassSpecies,
  ]);

  const handlePermissions = useMemo(() => {
    return new UserPermissions(user.value);
  }, [user.value]);

  const selectedSpecies = useMemo(() => {
    if (species.value && forestMassSpecies?.length) {
      const speciesUuid = forestMassSpecies.map((fms) => fms.speciesUuid);
      return species.value.filter(({uuid}) => speciesUuid.includes(uuid));
    } else {
      return [];
    }
  }, [species.value, forestMassSpecies]);

  const forestMassStatus = useMemo(() => {
    if (uuid && forestMassStatuses && forestMassStatuses.length) {
      return forestMassStatuses.find((fm) => fm.forestMassUuid === uuid);
    } else {
      return undefined;
    }
  }, [uuid, forestMassStatuses]);

  const businessLogic = useMemo(() => {
    if (forestMassStatus) {
      return new businessLogicBasedOnStatuses(forestMassStatus);
    }
  }, [forestMassStatus]);

  const handleUpdateForestMass = async (entity: ForestMass) => {
    await updateForestMassUseCase.execute(entity).then(setForestMass);
  };

  const handleCutForestMass = async (entity: CutForestMass) => {
    const {forestMass, forestMassStatuses} = await opCutForestMassUseCase.execute(entity);
    if (forestMass) setForestMass(forestMass);
    if (forestMassStatuses) setForestMassStatuses(forestMassStatuses);
  };

  const handleDownloadGeopackage = (forestMass: ForestMass) => {
    downloadGpkgForestMassUseCase.execute(forestMass);
  };

  const handleUploadInventoryPlot = async (domain: CreateInventoryPlot, file: File) => {
    try {
      await opCreateInventoryPlot.execute(domain, file);
      await fetchInventoryPlots();
      await fetchForestMassStatuses();
    } catch (error) {
      publishErrorUseCase.execute(error as Error);
    }
  };

  const handleUploadUavPointCloud = async (domain: Partial<UavPointCloudCreate>, file: File) => {
    const entity = {
      ...domain,
      forestMassUuid: forestMass?.uuid,
    } as UavPointCloudCreate;

    try {
      setLoading(true);
      await createUavPointCloudsUseCase.execute(entity, file);
    } catch (error) {
      publishErrorUseCase.execute(error as Error);
    } finally {
      setLoading(false);
    }
    await fetchUavPointClouds();
    await fetchForestMassStatuses();
  };

  const handleDeleteUavPointCloud = async (pointCloudUuid: string) => {
    await deleteUavPointCloudsUseCase.execute(pointCloudUuid);
    await fetchUavPointClouds();
    await fetchForestMassStatuses();
  };

  const handleDownloadUavPointCloud = async (pointCloudUuid: string) => {
    await downloadUavPointCloud.execute(pointCloudUuid);
  };

  const handleReuploadUavPointCloud = async (pointCloudUuid: string, file: File) => {
    try {
      setLoading(true);
      await reUploadUavPointCloud.execute(pointCloudUuid, file);
    } catch (error) {
      publishErrorUseCase.execute(error as Error);
    } finally {
      setLoading(false);
    }
    await fetchUavPointClouds();
    await fetchForestMassStatuses();
  };

  const handleBack = () => {
    const from = (location.state as BackState | null)?.from;
    navigate(from ?? '../../forest-masses', {replace: true});
  };

  const handleInventoryPlotSelect = (uuid: string) =>
    navigate(`../../inventory-plots/${uuid}`, {
      state: {from: `${location.pathname}${location.search}`, backState: location.state},
    });

  const handleSelectedSpeciesChange = async (species_uuids: Array<string>) => {
    if (forestMass?.uuid && forestMassSpecies) {
      const updatedForestMassSpecies = await updateForestMassSpeciesUseCase.execute(
        forestMass?.uuid,
        species_uuids,
        forestMassSpecies,
        selectedSpecies,
      );
      setForestMassSpecies(updatedForestMassSpecies);
      await fetchForestMassStatuses();
    }
  };

  return {
    forestMass: forestMass,
    forestMassStatus,
    forestMassSpecies,
    selectedSpecies: selectedSpecies,
    appraisers: appraisers.value || [],
    species: species.value || [],
    inventoryPlots: inventoryPlots.value || [],
    lidarTechnologies: lidarTechnologies.value,
    uavPointCloud: uavPointCloud.value || undefined,
    loading,
    userPermissions: handlePermissions,
    businessLogic: businessLogic,
    onDownloadGeopackage: handleDownloadGeopackage,
    onUpdateForestMass: handleUpdateForestMass,
    onCutForestMass: handleCutForestMass,
    onBack: handleBack,
    onInventoryPlotSelect: handleInventoryPlotSelect,
    onSpeciesChange: handleSelectedSpeciesChange,
    onUploadInventoryPlot: handleUploadInventoryPlot,
    onUploadUavPointCloud: handleUploadUavPointCloud,
    onDeleteUavPointCloud: handleDeleteUavPointCloud,
    onDownloadUavPointCloud: handleDownloadUavPointCloud,
    onReuploadUavPointCloud: handleReuploadUavPointCloud,
  };
};
